import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import SocialAuth from './components/SocialAuth';

const LoginRegister = () => {
  const [activeTab, setActiveTab] = useState('login');
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard-overview');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleAuthSuccess = () => {
    navigate('/dashboard-overview');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-text-secondary">Loading Arkdai...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-background to-secondary-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-6xl">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <Icon name="Zap" size={24} color="white" />
            </div>
            <h1 className="text-2xl font-bold text-text-primary">Arkdai</h1>
          </div>
          <p className="text-text-secondary text-lg">
            Streamline your development workflow with powerful project management
          </p>
        </div>

        <div className="bg-surface rounded-2xl shadow-xl border border-border overflow-hidden">
          <div className="border-b border-border bg-secondary-50">
            <div className="flex">
              <button onClick={() => setActiveTab('login')}
                className={`flex-1 py-4 px-6 text-center font-medium transition-all duration-200 ${
                  activeTab === 'login' ? 'text-primary border-b-2 border-primary bg-surface' : 'text-text-secondary hover:text-text-primary hover:bg-secondary-100'
                }`}>
                <div className="flex items-center justify-center space-x-2">
                  <Icon name="LogIn" size={20} />
                  <span>Sign In</span>
                </div>
              </button>
              <button onClick={() => setActiveTab('register')}
                className={`flex-1 py-4 px-6 text-center font-medium transition-all duration-200 ${
                  activeTab === 'register' ? 'text-primary border-b-2 border-primary bg-surface' : 'text-text-secondary hover:text-text-primary hover:bg-secondary-100'
                }`}>
                <div className="flex items-center justify-center space-x-2">
                  <Icon name="UserPlus" size={20} />
                  <span>Create Account</span>
                </div>
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 min-h-[600px]">
            <div className="p-8 lg:p-12">
              <div className="max-w-md mx-auto">
                <div className="mb-8">
                  <h2 className="text-2xl font-semibold text-text-primary mb-2">
                    {activeTab === 'login' ? 'Welcome back!' : 'Get started today'}
                  </h2>
                  <p className="text-text-secondary">
                    {activeTab === 'login' ?'Sign in to your Arkdai account to continue managing your projects' :'Create your account and join teams already using Arkdai'
                    }
                  </p>
                </div>
                {activeTab === 'login' ? (
                  <LoginForm onSuccess={handleAuthSuccess} />
                ) : (
                  <RegisterForm onSuccess={handleAuthSuccess} />
                )}
                <SocialAuth onSuccess={handleAuthSuccess} />
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary-500 to-primary-700 p-8 lg:p-12 text-white flex flex-col justify-center">
              <div className="max-w-md mx-auto">
                <h3 className="text-2xl font-semibold mb-6">
                  {activeTab === 'login' ? 'Continue your productivity journey' : 'Join the future of project management'}
                </h3>
                <div className="space-y-6">
                  {[
                    { icon: 'Kanban', title: 'Visual Task Management', desc: 'Organize your work with intuitive Kanban boards and drag-and-drop functionality' },
                    { icon: 'Calendar', title: 'Agile Sprint Planning', desc: 'Plan and track sprints with advanced analytics and velocity forecasting' },
                    { icon: 'Users', title: 'Real-time Collaboration', desc: 'Work together seamlessly with live updates and team synchronization' },
                    { icon: 'BarChart3', title: 'Advanced Analytics', desc: 'Get insights into team performance with comprehensive reporting tools' },
                  ]?.map((feature) => (
                    <div key={feature?.icon} className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name={feature?.icon} size={18} color="white" />
                      </div>
                      <div>
                        <h4 className="font-medium mb-1">{feature?.title}</h4>
                        <p className="text-primary-100 text-sm">{feature?.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 p-4 bg-white bg-opacity-10 rounded-lg">
                  <p className="text-sm italic mb-3">
                    "Arkdai transformed how our team manages projects. The sprint planning features are incredible!"
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium">SM</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Sarah Mitchell</p>
                      <p className="text-xs text-primary-100">Product Manager at TechCorp</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-8 text-sm text-text-secondary">
          <p>
            By continuing, you agree to our{' '}
            <button className="text-primary hover:text-primary-700 transition-colors duration-200">Terms of Service</button>{' '}and{' '}
            <button className="text-primary hover:text-primary-700 transition-colors duration-200">Privacy Policy</button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginRegister;